export interface PaginationControlsProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  itemLabel?: string;
  variant?: "default" | "admin";
}

function buildVisiblePages(currentPage: number, totalPages: number) {
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const startPage = Math.max(1, Math.min(currentPage - 2, totalPages - 4));
  return Array.from({ length: 5 }, (_, index) => startPage + index);
}

export default function PaginationControls({
  currentPage,
  totalPages,
  onPageChange,
  itemLabel = "éléments",
  variant = "default"
}: PaginationControlsProps) {
  if (totalPages <= 1) {
    return null;
  }

  const visiblePages = buildVisiblePages(currentPage, totalPages);

  if (variant === "admin") {
    return (
      <div className="flex flex-col items-center gap-3 rounded-2xl border border-orange-100 bg-white px-4 py-4">
        <div className="flex flex-wrap items-center justify-center gap-2">
          <button
            type="button"
            disabled={currentPage === 1}
            onClick={() => onPageChange(1)}
            className="flex h-10 w-10 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-orange-50 hover:text-[#E8521A] disabled:cursor-not-allowed disabled:opacity-35"
            aria-label="Première page"
          >
            <i className="ri-skip-left-line text-lg"></i>
          </button>
          <button
            type="button"
            disabled={currentPage === 1}
            onClick={() => onPageChange(Math.max(1, currentPage - 1))}
            className="flex h-10 w-10 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-orange-50 hover:text-[#E8521A] disabled:cursor-not-allowed disabled:opacity-35"
            aria-label="Page précédente"
          >
            <i className="ri-arrow-left-s-line text-lg"></i>
          </button>

          {visiblePages.map((page) => {
            const isActive = page === currentPage;

            return (
              <button
                key={page}
                type="button"
                onClick={() => onPageChange(page)}
                className={`flex h-11 w-11 items-center justify-center rounded-full text-sm font-semibold transition-all ${
                  isActive
                    ? "bg-[#E8521A] text-white shadow-[0_12px_30px_rgba(232,82,26,0.22)]"
                    : "text-gray-600 hover:bg-orange-50 hover:text-[#E8521A]"
                }`}
                aria-current={isActive ? "page" : undefined}
              >
                {page}
              </button>
            );
          })}

          <button
            type="button"
            disabled={currentPage === totalPages}
            onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
            className="flex h-10 w-10 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-orange-50 hover:text-[#E8521A] disabled:cursor-not-allowed disabled:opacity-35"
            aria-label="Page suivante"
          >
            <i className="ri-arrow-right-s-line text-lg"></i>
          </button>
          <button
            type="button"
            disabled={currentPage === totalPages}
            onClick={() => onPageChange(totalPages)}
            className="flex h-10 w-10 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-orange-50 hover:text-[#E8521A] disabled:cursor-not-allowed disabled:opacity-35"
            aria-label="Dernière page"
          >
            <i className="ri-skip-right-line text-lg"></i>
          </button>
        </div>

        <p className="text-sm text-gray-500">
          Page <span className="font-semibold text-[#1A1A1A]">{currentPage}</span> sur{" "}
          <span className="font-semibold text-[#1A1A1A]">{totalPages}</span>
          {" · "}
          {itemLabel}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-gray-100 bg-white px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
      <p className="text-sm text-gray-500">
        Page <span className="font-semibold text-[#1A1A1A]">{currentPage}</span> sur{" "}
        <span className="font-semibold text-[#1A1A1A]">{totalPages}</span>
        {" · "}
        {itemLabel}
      </p>
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={currentPage === 1}
          onClick={() => onPageChange(Math.max(1, currentPage - 1))}
          className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-medium text-gray-600 transition-colors hover:border-[#E8521A] hover:text-[#E8521A] disabled:cursor-not-allowed disabled:opacity-40"
        >
          <i className="ri-arrow-left-s-line mr-1"></i>
          Page précédente
        </button>
        <button
          type="button"
          disabled={currentPage === totalPages}
          onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
          className="rounded-xl bg-[#E8521A] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#d44a16] disabled:cursor-not-allowed disabled:opacity-40"
        >
          Page suivante
          <i className="ri-arrow-right-s-line ml-1"></i>
        </button>
      </div>
    </div>
  );
}
